/**
 * Australian financial calculations (2024-25 Stage 3 tax rates).
 */

/**
 * Calculates resident individual income tax for 2024-25.
 * Rates:
 * 0 – $18,200: Nil
 * $18,201 – $45,000: 16%
 * $45,001 – $135,000: $4,288 + 30% of excess over $45,000
 * $135,001 – $190,000: $31,288 + 37% of excess over $135,000
 * $190,001 and over: $51,638 + 45% of excess over $190,000
 *
 * Note: Does not include Medicare Levy (usually 2%).
 */
export function calculateTaxAU2024(taxableIncomeCents: number): number {
  const income = Math.max(0, taxableIncomeCents / 100);
  let tax = 0;

  if (income <= 18200) {
    tax = 0;
  } else if (income <= 45000) {
    tax = (income - 18200) * 0.16;
  } else if (income <= 135000) {
    tax = 4288 + (income - 45000) * 0.30;
  } else if (income <= 190000) {
    tax = 31288 + (income - 135000) * 0.37;
  } else {
    tax = 51638 + (income - 190000) * 0.45;
  }

  return Math.round(tax * 100); // Return in cents
}

/**
 * Calculates net surplus based on income, expenses, and tax.
 */
export function calculateFinancialSurplus(params: {
  annualIncomeCents: number;
  lifestyleExpensesCents: number;
  taxPayableCents?: number; // If provided, use this; otherwise calculate
}): number {
  const tax = params.taxPayableCents ?? calculateTaxAU2024(params.annualIncomeCents);
  return params.annualIncomeCents - params.lifestyleExpensesCents - tax;
}


/**
 * Per-household-member derived figures, mirroring the Juggler worksheet:
 *   base       = income(incl super) - concessional super + investment income - investment expenses
 *   netIncome  = base - taxPayable           (taxPayable is the override if given, else computed on base)
 *   surplus    = netIncome - lifestyle expenses
 * All inputs/outputs in integer cents. Nulls treated as 0.
 */
export function deriveMemberFinancials(m: {
  incomeIncSuperCents: number | null;
  concessionalSuperCents: number | null;
  investmentIncomeCents: number | null;
  investmentExpensesCents: number | null;
  taxPayableCents: number | null;
  lifestyleExpensesCents: number | null;
}): { taxPayableCents: number; netIncomeCents: number; surplusCents: number } {
  const income = m.incomeIncSuperCents ?? 0;
  const concSuper = m.concessionalSuperCents ?? 0;
  const invIncome = m.investmentIncomeCents ?? 0;
  const invExpenses = m.investmentExpensesCents ?? 0;
  const lifestyle = m.lifestyleExpensesCents ?? 0;
  const baseCents = income - concSuper + invIncome - invExpenses;
  const taxPayableCents = m.taxPayableCents ?? calculateTaxAU2024(Math.max(0, baseCents));
  const netIncomeCents = baseCents - taxPayableCents;
  const surplusCents = netIncomeCents - lifestyle;
  return { taxPayableCents, netIncomeCents, surplusCents };
}


/**
 * Household fee + portfolio metrics (the Juggler worksheet's right column).
 * All cents. `adviserFeeRate` and per-account `feeRate` are decimals (0.0075 = 0.75%).
 *   estimatedYearlyFee = Σ(account value × its feeRate)   — what's actually charged
 *   targetYearlyFee    = total FUM × household adviserFeeRate — the agreed rate
 *   feeVariance        = estimated − target ($ and % of target)
 */
export function computeFeeMetrics(params: {
  accounts: Array<{ valueCents: number; feeRate: number }>;
  adviserFeeRate: number;
  cashCents: number;
  trailing12moBrokerageCents: number;
}): {
  totalFumCents: number;
  cashToInvestCents: number;
  estimatedYearlyFeeCents: number;
  targetYearlyFeeCents: number;
  feeVarianceCents: number;
  feeVariancePct: number;
  trailing12moBrokerageCents: number;
} {
  const totalFumCents = params.accounts.reduce((sum, a) => sum + a.valueCents, 0);
  const estimatedYearlyFeeCents = Math.round(
    params.accounts.reduce((sum, a) => sum + a.valueCents * a.feeRate, 0),
  );
  const targetYearlyFeeCents = Math.round(totalFumCents * params.adviserFeeRate);
  const feeVarianceCents = estimatedYearlyFeeCents - targetYearlyFeeCents;
  const feeVariancePct = targetYearlyFeeCents > 0 ? feeVarianceCents / targetYearlyFeeCents : 0;
  return {
    totalFumCents,
    cashToInvestCents: params.cashCents,
    estimatedYearlyFeeCents,
    targetYearlyFeeCents,
    feeVarianceCents,
    feeVariancePct,
    trailing12moBrokerageCents: params.trailing12moBrokerageCents,
  };
}
